import { Fragment, useEffect, useRef, useState, type ReactNode } from 'react';
import { browserSessionInvalidated, browserSessionRequest, setBrowserSession, type BrowserSession } from '../api/browser-session';
import { getTelegramInitData, readDevTelegramIdFromQuery } from '@/shared/telegram';
import { detectLocale } from '@/i18n';
import './browser-entry.css';

declare global { interface Window { autoheroTelegramLogin?: (payload: unknown) => void } }

export function BrowserEntryContainer({ children }: { children: ReactNode }) {
  // Mini App signatures and the existing permitted local preview keep their own bootstrap.
  const bypass = Boolean(getTelegramInitData()) || readDevTelegramIdFromQuery() !== null ||
    ['localhost', '127.0.0.1', '[::1]', '::1'].includes(window.location.hostname);
  const [session, setSession] = useState<BrowserSession | null>(null);
  const [error, setError] = useState(false);
  const [retry, setRetry] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const widget = useRef<HTMLDivElement>(null);
  const ru = detectLocale() === 'ru';

  useEffect(() => {
    if (bypass) return;
    const reset = () => { setSession(null); setRetry(value => value + 1); };
    window.addEventListener(browserSessionInvalidated, reset);
    return () => window.removeEventListener(browserSessionInvalidated, reset);
  }, [bypass]);

  useEffect(() => {
    if (bypass) return;
    const controller = new AbortController();
    setError(false);
    setBrowserSession(null);
    browserSessionRequest('session', { signal: controller.signal }).then((value) => {
      if (controller.signal.aborted) return;
      setBrowserSession(value.authenticated ? { telegramId: value.telegramId!, csrfToken: value.csrfToken } : null);
      setSession(value);
    }).catch(() => { if (!controller.signal.aborted) setError(true); });
    return () => controller.abort();
  }, [bypass, retry]);

  useEffect(() => {
    if (bypass || !session?.authenticated) return;
    const controller = new AbortController();
    let checking = false;
    const refresh = async () => {
      if (checking) return;
      checking = true;
      try {
        const value = await browserSessionRequest('session', { signal: controller.signal });
        if (controller.signal.aborted) return;
        if (!value.authenticated || value.telegramId !== session.telegramId || value.csrfToken !== session.csrfToken) {
          setBrowserSession(value.authenticated ? { telegramId: value.telegramId!, csrfToken: value.csrfToken } : null);
          setSession(value);
        }
      } catch { /* The server still fences commands during network or database outages. */ }
      finally { checking = false; }
    };
    // WS reconnect hides handshake status; recheck the authoritative session even while idle.
    const timer = window.setInterval(() => { void refresh(); }, 60_000);
    const onFocus = () => { void refresh(); };
    window.addEventListener('focus', onFocus);
    return () => { controller.abort(); window.clearInterval(timer); window.removeEventListener('focus', onFocus); };
  }, [bypass, session]);

  useEffect(() => {
    if (bypass || !session || session.authenticated || !session.telegramBotUsername || !widget.current) return;
    const controller = new AbortController();
    let busy = false;
    window.autoheroTelegramLogin = (payload: unknown) => {
      if (busy || controller.signal.aborted) return;
      busy = true;
      setSubmitting(true);
      setError(false);
      browserSessionRequest('telegram', { payload, csrfToken: session.csrfToken, signal: controller.signal }).then((value) => {
        if (controller.signal.aborted) return;
        if (!value.authenticated) throw new Error('Login rejected');
        setBrowserSession({ telegramId: value.telegramId!, csrfToken: value.csrfToken });
        setSession(value);
      }).catch(() => { if (!controller.signal.aborted) setError(true); }).finally(() => {
        if (!controller.signal.aborted) { busy = false; setSubmitting(false); }
      });
    };
    const host = widget.current;
    const script = document.createElement('script');
    script.src = 'https://telegram.org/js/telegram-widget.js?22';
    script.async = true;
    script.setAttribute('data-telegram-login', session.telegramBotUsername);
    script.setAttribute('data-size', 'large');
    script.setAttribute('data-onauth', 'autoheroTelegramLogin(user)');
    script.onerror = () => { if (!controller.signal.aborted) setError(true); };
    host.replaceChildren(script);
    return () => { controller.abort(); delete window.autoheroTelegramLogin; host.replaceChildren(); };
  }, [bypass, session]);

  if (bypass) return children;
  if (session?.authenticated) return <Fragment key={`${session.telegramId}:${session.csrfToken}`}>{children}</Fragment>;
  return <main className="th-game th-browser-entry">
    <section className="th-browser-entry-content" aria-labelledby="login-title">
      <h1 id="login-title">AutoHero</h1>
      <p>{ru ? 'Войдите через Telegram, чтобы продолжить приключение своего героя.' : 'Sign in with Telegram to continue your hero’s adventure.'}</p>
      {!session && !error && <p role="status">{ru ? 'Проверяем вход…' : 'Checking your session…'}</p>}
      <div ref={widget} aria-busy={submitting} />
      {submitting && <p role="status">{ru ? 'Входим…' : 'Signing in…'}</p>}
      {session && !session.telegramBotUsername && <p role="status">{ru ? 'Вход в браузере пока недоступен. Откройте игру в Telegram.' : 'Browser login is currently unavailable. Open the game in Telegram.'}</p>}
      {error && <div role="alert"><p>{ru ? 'Не удалось войти. Попробуйте ещё раз.' : 'Unable to sign in. Please try again.'}</p><button type="button" onClick={() => { setSession(null); setRetry(value => value + 1); }}>{ru ? 'Повторить вход' : 'Retry sign in'}</button></div>}
    </section>
  </main>;
}
